import {makeAutoObservable, runInAction} from 'mobx';
import {
    IPlanEditorModel,
    IPlanEditorEventStory,
    PLAN_EDITOR_EVENTS,
    removeNotAllowedPlanFields
} from '../../../models/Plan';
import SubscriptionPlansService from '../../../services/api/subscription-plans-service';
import {rootContainer, TYPE} from '../../../services/container/root';
import {uuid} from '../../../services/helpers';
import {ISaveable} from '../../../stores/types';
import {IOption} from "../../../components/inputs/select-dropdown/select-dropdown";
import { IInputValitationResponse } from '../../../hooks/input-validation-hook';
import { FeatureListManager } from './features-list-manager';
import { InstallmentListManager, INSTALLMENTS_BREAKDOWN_TYPES } from './installment-list-manager';
import { getNullableChangesOrModel, isSet } from '../../../models/model-types-methods';


/** makes same interface as IPlanEditorModel but with all fields optional */
export interface IPlanEditorModelChanges extends Partial<Omit<IPlanEditorModel, 'period'|'price'|'capped_amount'|'price_per_extra_rma'|'rma_quota'|'trial_days'>> {
    _clientId?: string;
    period?: number|null;
    price?: number|null;
    capped_amount?: number|null;
    price_per_extra_rma?: number|null;
    rma_quota?: number|null;
    trial_days?: number|null;
}

export enum PLAN_CHARGE_TYPES {
    RECURRING = 'RECURRING',
    ANNUAL = 'ANNUAL',
    LONG_TERM = 'LONG_TERM'
}

export const PLAN_CHARGE_TYPE_OPTIONS: IOption[] = [
     {label: 'Monthly', value: PLAN_CHARGE_TYPES.RECURRING},
     {label: 'Annual', value: PLAN_CHARGE_TYPES.ANNUAL},
     {label: 'Installments', value: PLAN_CHARGE_TYPES.LONG_TERM}
];

export const INSTALLMENT_BREAKDOWN_TYPE_OPTIONS: IOption[] = [
    {label: 'Even', value: INSTALLMENTS_BREAKDOWN_TYPES.EVEN},
    {label: 'Custom', value: INSTALLMENTS_BREAKDOWN_TYPES.CUSTOM},
    /** https://returngo.atlassian.net/browse/RD-6422 */
    // {label: 'Absolute', value: INSTALLMENTS_BREAKDOWN_TYPES.ABSOLUTE}, /** uncomment when we will remove dependency in immediate installment for LT plans */
]

class SubscriptionPlanEditorStore implements ISaveable {
    private _model: IPlanEditorModel = {} as IPlanEditorModel;
    private _editingStory: IPlanEditorEventStory[] = [];
    private _featureListManager: FeatureListManager;

    private _changes: IPlanEditorModelChanges = {};
    /** this property is set by UI and from constructor and setChanges, when plan changes are discarded - we set to undefined */
    private _pseudoType?: PLAN_CHARGE_TYPES;
    private _isSaving: boolean = false;
    private _installmentListManager: InstallmentListManager;

    /** TODO: model should never be exposed like this */
    public get model(): IPlanEditorModel {
        return this._model;
    }

    public get featuresList(): FeatureListManager { return this._featureListManager; }
    public get installmentsList(): InstallmentListManager { return this._installmentListManager; }

    constructor(private _planPagedListDataActualiser: (savedPlan: IPlanEditorModel) => void, model?: IPlanEditorModel) {
        makeAutoObservable(this, {}, {autoBind: true});

        if (model) {
            this._model = ensureValidModelCopy(model);
        } else {
            /** when creating new plan from scratch */
            this._changes = {
                _clientId: uuid(),
            }
        }

        this._featureListManager = new FeatureListManager(this, model?.features);
        this._installmentListManager = new InstallmentListManager(this, model?.installments);

        this._pseudoType = this._model?.type;
        if((this._model?.installments?.length ?? 0) > 0) {
            this._pseudoType = PLAN_CHARGE_TYPES.LONG_TERM;
        }
    }

    async fetchEditingStory(planId?: number ) {
        try {
            if (planId) {
                const res = await this.service.getPlanEditingEvents(planId);
                runInAction(() => {
                    this._setEditingStory(res);
                });
            }
        } catch { }
    }

    public getUpdatedByUser(): IPlanEditorEventStory | undefined {
        return this._editingStory.slice()
            .sort((next, prev) =>  (prev.id < next.id) ? -1 : 1)
            .find((story) =>
            story.action === PLAN_EDITOR_EVENTS.UPDATE
        );
    }

    public getCreatedByUser(): IPlanEditorEventStory | undefined {
        return this._editingStory.find((story) =>
            story.action === PLAN_EDITOR_EVENTS.CREATE
        );
    }

    private _setEditingStory(editingEvents: IPlanEditorEventStory[]) {
        this._editingStory = editingEvents;
    }

    public setType(value: PLAN_CHARGE_TYPES) {
        this._pseudoType = value;
        this._changes.type = value;

        if (value === PLAN_CHARGE_TYPES.LONG_TERM) {
            this._changes.type = PLAN_CHARGE_TYPES.ANNUAL;
        }

        this.installmentsList._generateEvenInstallmentsIfNeeded();
        this._handlePlanChanges();
    }

    /** method that should be called when some inputs are changed and we need to make other changes accordingly */
    private _handlePlanChanges(): void {
        this._featureListManager.handlePlanChanges();
        if (this.isTrialDaysDisabled) {
            this.setTrialDays(0);
        }
    }

    public get type(): PLAN_CHARGE_TYPES {
        return this._pseudoType ?? this._changes?.type ?? this._model?.type;
    }

    public setPeriod(value: number | null) {
        this._changes.period = value;
        this.installmentsList._generateEvenInstallmentsIfNeeded();
    }

    public get period(): number | undefined | null {
        return getNullableChangesOrModel(this._changes?.period, this._model?.period);
    }

    public get featuresInfo(): string {
        return this._featureListManager.getFeaturesInfo();
    }

    public get featuresCount(): number {
        return this._featureListManager.getFeaturesCount();
    }

    public discardChanges(): void {
        this._featureListManager.discardChanges();
        this._changes = {};
        this._pseudoType = this._model?.type;
        if((this._model?.installments?.length ?? 0) > 0) {
            this._pseudoType = PLAN_CHARGE_TYPES.LONG_TERM;
        }        
        this._installmentListManager = new InstallmentListManager(this, this._model?.installments);
    }

    public setChanges(changes: IPlanEditorModelChanges): void {
        if (!changes.type) {
            changes.type = PLAN_CHARGE_TYPES.RECURRING;
        }
        this._changes = changes;        
        
        this._featureListManager.setChanges(this._changes.features);        
        this._installmentListManager.setChanges(this._changes.installments);

        this._pseudoType = this._changes.type;
        if((this._changes.installments?.length ?? 0) > 0) {
            this._pseudoType = PLAN_CHARGE_TYPES.LONG_TERM;
        }
    }

    private _clearChangesIfEqual(key: keyof IPlanEditorModel): void {
        if (this._changes[key] === this._model[key] ||
            (this._model[key] === null && this._changes[key] === undefined)) {
            delete this._changes[key];
        }
    }

    public hasChanges(): boolean {
        const featuresHasChanges = this._featureListManager.hasChanges();
        const installmentsHasChanges = this._installmentListManager.hasChanges();
        return featuresHasChanges || installmentsHasChanges || !!Object.keys(this._changes).length;
    }

    public canSave(): boolean {
        const hasInstallmentInEditMode = this.type === PLAN_CHARGE_TYPES.LONG_TERM && this.installmentsList.isInEditMode();
        return !hasInstallmentInEditMode && this.hasChanges();
    }

    public hasConsumptionPeriod(): boolean {
        return this.type !== PLAN_CHARGE_TYPES.RECURRING;
    }
    public isChanged(key: keyof IPlanEditorModel) {
        if (typeof this._changes[key] === 'undefined') {
            return false;
        }
        return this._changes[key] !== this._model[key];
    }

    public getUpdatedModel(): IPlanEditorModel {
        const features = this._featureListManager.getUpdatedModel();

        let data: IPlanEditorModel = {
            ...this._model, 
            ...this._changes, 
            features, 
            installments: this._installmentListManager.getUpdatedModel(),
            _pseudo_type: this.type, /** might have value pseudo value LONG_TERM */
            _installments_break_down_type: this.installmentsList.installmentBreakdownType,
        } as IPlanEditorModel;

        data = removeNotAllowedPlanFields(data) as IPlanEditorModel;

        return data;
    }

    async save(): Promise<void> {
        const data = this.getUpdatedModel();
        let model: IPlanEditorModel|undefined;
        this._isSaving = true;

        try {
            if (this._isNew()) {
                model = await this.service.create(data);
                this.service.markPlanAsNewlyCreated(model.id);
            } else {
                model = await this.service.update(this._model.id, data);
            }    
        }
        catch (e) {
            console.error(e);
        }

        runInAction(() => {
            if (model) {
                this._model = ensureValidModelCopy(model!);
                this.discardChanges();
                this._featureListManager = new FeatureListManager(this, model.features);
                this._installmentListManager = new InstallmentListManager(this, model!.installments);

                this._planPagedListDataActualiser(this._model);
            }
            this._isSaving = false;
        });

    }

    async refresh(): Promise<void> {
        if(!this.id) {
            console.error('invalid attempt to refresh not yet saved plan');
            return;
        }

        this._isSaving = true;

        try {
            const model = await this.service.fetchPlan(this.id);
            runInAction(() => {
                this._model = ensureValidModelCopy(model);
                this.discardChanges();
                this._featureListManager = new FeatureListManager(this, model.features);
                this._installmentListManager = new InstallmentListManager(this, model!.installments);
                this._planPagedListDataActualiser(this._model);
                this._isSaving = false;
            });
        }
        catch(e) {
            runInAction(() => {
                this._isSaving = false;
            });
        }
    }

    public isSaving(): boolean {
        return this._isSaving;
    }

    private _isNew(): boolean {
        return !this._model?.id;
    }

    private get service(): SubscriptionPlansService {
        return rootContainer.get<SubscriptionPlansService>(TYPE.SubscriptionPlansService);
    }

    public get id(): number | undefined {
        return this._model?.id;
    }

    public get isInUse(): boolean {
        return this._model?.in_use ?? false;
    }

    public get name(): string {
        return this._changes?.name ?? this._model?.name;
    }

    public setName(value: string) {
        this._changes.name = value;
        this._clearChangesIfEqual('name');
    }

    public get price(): number | undefined | null {
        return getNullableChangesOrModel(this._changes?.price, this._model?.price);
    }

    public validatePrice(): IInputValitationResponse {
        if (!isSet(this.price) || (this.price as number) < 0) {
            return {
                isValid: false,
                messages: ['price cannot be less than 0.00$'],
            };
        }

        return {
            isValid: true,
            messages: [],
        };
    }

    public setPrice(value: number|null) {
        this._changes.price = value;
        this.installmentsList._generateEvenInstallmentsIfNeeded();
        this._clearChangesIfEqual('price');
    }

    public get rma_quota(): number | null {
        return getNullableChangesOrModel(this._changes?.rma_quota, this._model?.rma_quota);
    }

    public setRmaQuota(value: number|null) {
        this._changes.rma_quota = isSet(value) ? Number(value) : value;
        this._clearChangesIfEqual('rma_quota');
        this.calculateCappedAmount();
    }

    public get price_per_extra_rma(): number | null {
        return getNullableChangesOrModel(this._changes?.price_per_extra_rma, this._model?.price_per_extra_rma);
    }

    public setPricePerExtraRma(value: number|null) {
        this._changes.price_per_extra_rma = isSet(value) ? Number(value) : value;
        this._clearChangesIfEqual('price_per_extra_rma');
        this.calculateCappedAmount();
    }

    public get capped_amount(): number | null {
        return getNullableChangesOrModel(this._changes?.capped_amount, this._model?.capped_amount);
    }

    public setCappedaAmount(value: number|null) {
        this._changes.capped_amount = isSet(value) ? Number(value) : value;
        this._clearChangesIfEqual('capped_amount');
    }

    public validateCappedAmount(): IInputValitationResponse {
        if ((this.capped_amount ?? 0) < 5) {
            return {
                isValid: false,
                messages: ['the minimum allowed for capped amount is 5']
            }
        }
        return {
            isValid: true,
            messages: [],
        };
}

    private calculateCappedAmount() {
        if (!isSet(this.price_per_extra_rma) || !isSet(this.rma_quota)) {
            return;
        }
        const calcCappedAmount = this.price_per_extra_rma! * this.rma_quota! * 10;
        this.setCappedaAmount(calcCappedAmount);
    }

    public get trialDays(): number | null {
        return getNullableChangesOrModel(this._changes?.trial_days, this._model?.trial_days);
    }

    public get isTrialDaysDisabled(): boolean {
        /** disabling trial days for not regular recurring types while we count on one-time charge */
        return this.type !== PLAN_CHARGE_TYPES.RECURRING;
    }

    public setTrialDays(value: number|null) {
        this._changes.trial_days = isSet(value) ? Number(value) : value;
        this._clearChangesIfEqual('trial_days');
    }

    public get assigned_stores(): number {
        return this._changes?.assigned_stores ?? this._model?.assigned_stores;
    }

    public get tag(): string | undefined {
        return this._changes?.tag ?? this._model?.tag;
    }

    public set tag(value: string | undefined) {
        this._changes.tag = value;
        this._clearChangesIfEqual('tag');
    }

    public get tag_color(): string | undefined {
        return this._changes?.tag_color ?? this._model?.tag_color;
    }

    public set tag_color(value: string | undefined) {
        this._changes.tag_color = value;
        this._clearChangesIfEqual('tag_color');
    }

    public get internal_name(): string | undefined {
        return this._changes?.internal_name ?? this._model?.internal_name;
    }

    public setInternalName(value: string) {
        this._changes.internal_name = value;
        this._clearChangesIfEqual('internal_name');
    }

    public get is_custom(): boolean {
        return this._changes?.is_custom ?? this._model?.is_custom;
    }

    public set is_custom(value: boolean) {
        this._changes.is_custom = value;
        this._clearChangesIfEqual('is_custom');

        if (this._changes.is_custom) {
            this.setOnInstall(false);
        }
    }

    public get deprecated(): boolean {
        return this._changes?.deprecated ?? this._model?.deprecated;
    }

    public set deprecated(value: boolean) {
        this._changes.deprecated = value;
        this._clearChangesIfEqual('deprecated');
    }

    public get on_install(): boolean {
        return this._changes?.on_install ?? this._model?.on_install;
    }

    public get is_test(): boolean {
        return this._changes?.test ?? this._model?.test;
    }

    public setIsTest(value: boolean): void {
        this._changes.test = value;
    }

    public setOnInstall(value: boolean) {
        this._changes.on_install = value;
        this._clearChangesIfEqual('on_install');
    }

    public get terms(): string | undefined {
        return this._changes?.terms ?? this._model?.terms;
    }

    public setTerms(value: string) {
        this._changes.terms = value;
        this._clearChangesIfEqual('terms');
    }

    public get plan_description(): string {
        return this._changes?.plan_description ?? this._model?.plan_description;
    }

    public setPlanDescription(value: string) {
        this._changes.plan_description = value;
        this._clearChangesIfEqual('plan_description');
    }

    public get short_description(): string {
        return this._changes?.short_description ?? this._model?.short_description;
    }

    public setShortDescription(value: string) {
        this._changes.short_description = value;
        this._clearChangesIfEqual('short_description');
    }

    public get created_at(): string {
        return this._model?.created_at ?? '';
    }

    public get updated_at(): string {
        return this._model?.updated_at ?? '';
    }

    public get isNewlyCreated() {
        return this.service.isNewlyCreatedPlan(this._model.id);
    }

    public get partnerId(): string|null {
        if (this._changes.partner_id === null) {
            return null;
        }
        return this._changes?.partner_id ?? this._model?.partner_id;
    }

    public setPartnerId(value: string|undefined) {
        this._changes.partner_id = value ? value : null;
        this._clearChangesIfEqual('partner_id');
    }
}

export default SubscriptionPlanEditorStore;


/** some fields receievd from an api has an incorrect type */
function ensureValidModelCopy(data: IPlanEditorModel): IPlanEditorModel {
    const toNum = (v: undefined | any) => v !== undefined ? Number(v) : v;
    const toBool = (v: undefined | any) => Boolean(v);

    return {
        ...data,
        id: toNum(data.id),
        price: toNum(data.price),
        rma_quota: toNum(data.rma_quota),
        price_per_extra_rma: toNum(data.price_per_extra_rma),
        capped_amount: toNum(data.capped_amount),
        trial_days: toNum(data.trial_days),
        assigned_stores: toNum(data.assigned_stores),
        on_install: toBool(data.on_install),
        is_custom: toBool(data.is_custom),
        deprecated: toBool(data.deprecated),
        in_use: toBool(data.in_use)
    };
}
